import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import {
  Bell, Moon, Sun, LogOut, LayoutDashboard, Users, Sparkles, Images, Award,
  Swords, Lightbulb, Library, FileText, Settings, School, UserCog, Medal, Trophy, BookOpen,
  ClipboardList, PieChart, Briefcase, KeyRound, Menu, X, BarChart3, ArrowLeftRight, Send, ShieldCheck, Inbox, ChevronDown, Eye,
} from "lucide-react";

const coordNav = [
  { to: "/portal", label: "لوحة التحكم", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/portal/students", label: "الطلبة المتطوعون", icon: Users, tid: "nav-students",
    children: [
      { to: "/portal/students#grade-أول ثانوي", label: "طلاب أول ثانوي", tid: "nav-students-g1" },
      { to: "/portal/students#grade-ثاني ثانوي", label: "طلاب ثاني ثانوي", tid: "nav-students-g2" },
      { to: "/portal/students#grade-ثالث ثانوي", label: "طلاب ثالث ثانوي", tid: "nav-students-g3" },
    ] },
  { to: "/portal/forty-hours", label: "متجاوزو 40 ساعة", icon: Medal, tid: "nav-forty" },
  { to: "/portal/staff", label: "الموظفون المتطوعون", icon: Briefcase, tid: "nav-staff" },
  { to: "/portal/opportunities", label: "الفرص التطوعية", icon: Sparkles, tid: "nav-opportunities" },
  { to: "/portal/gallery", label: "معرض الأعمال", icon: Images, tid: "nav-gallery" },
  { to: "/portal/certificates", label: "سجل الشهادات والأوسمة", icon: Award, tid: "nav-certificates" },
  { to: "/portal/challenges", label: "التحديات الأسبوعية", icon: Swords, tid: "nav-challenges" },
  { to: "/portal/proposals", label: "المقترحات والتصويت", icon: Lightbulb, tid: "nav-proposals" },
  { to: "/portal/circulars", label: "التعاميم الواردة", icon: Inbox, tid: "nav-circulars" },
  { to: "/portal/resources", label: "مكتبة الموارد", icon: Library, tid: "nav-resources" },
  { to: "/portal/reports", label: "التقارير", icon: FileText, tid: "nav-reports" },
  { to: "/portal/settings", label: "إعدادات المدرسة", icon: Settings, tid: "nav-settings" },
];

const adminNav = [
  { to: "/admin", label: "لوحة التحكم", icon: LayoutDashboard, end: true, tid: "nav-admin-dashboard" },
  { to: "/admin/schools", label: "المدارس والشرائح", icon: School, tid: "nav-admin-schools",
    children: [
      { to: "/admin/schools#tier-students-small", label: "الثانوية الصغرى", tid: "nav-admin-schools-sec-small" },
      { to: "/admin/schools#tier-students-medium", label: "الثانوية المتوسطة", tid: "nav-admin-schools-sec-medium" },
      { to: "/admin/schools#tier-students-large", label: "الثانوية الكبرى", tid: "nav-admin-schools-sec-large" },
      { to: "/admin/schools#stage-متوسطة", label: "المتوسطة", tid: "nav-admin-schools-stage-middle" },
      { to: "/admin/schools#stage-ابتدائية", label: "الابتدائية", tid: "nav-admin-schools-stage-elementary" },
      { to: "/admin/schools#stage-روضة", label: "رياض الأطفال", tid: "nav-admin-schools-stage-kindergarten" },
    ] },
  { to: "/admin/opportunities", label: "سجل الفرص", icon: ClipboardList, tid: "nav-admin-opportunities" },
  { to: "/admin/analytics", label: "تحليل القيم والأثر", icon: PieChart, tid: "nav-admin-analytics" },
  { to: "/admin/compare", label: "الإحصاءات والمقارنات", icon: BarChart3, tid: "nav-admin-compare" },
  { to: "/admin/forty-hours", label: "متجاوزو 40 ساعة", icon: Medal, tid: "nav-admin-forty" },
  { to: "/admin/coordinators", label: "المنسقون", icon: UserCog, tid: "nav-admin-coordinators" },
  { to: "/admin/supervisors", label: "المشرفون", icon: Eye, tid: "nav-admin-supervisors" },
  { to: "/admin/circulars", label: "التعاميم الصادرة", icon: Send, tid: "nav-admin-circulars" },
  { to: "/admin/pledges", label: "سجل التعهدات", icon: ShieldCheck, tid: "nav-admin-pledges" },
  { to: "/admin/certificates", label: "الشهادات الشهرية", icon: Medal, tid: "nav-admin-certificates" },
  { to: "/admin/challenges", label: "التحديات", icon: Trophy, tid: "nav-admin-challenges" },
  { to: "/admin/resources", label: "مكتبة الموارد", icon: BookOpen, tid: "nav-admin-resources" },
  { to: "/admin/marquee", label: "شريط الصور المتحرك", icon: Images, tid: "nav-admin-marquee" },
];

function NotificationBell({ notifBase }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  const load = () => api.get(notifBase).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      api.post(`${notifBase}/read-all`).then(load).catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell-btn" onClick={toggle}
        className="relative w-10 h-10 rounded-full bg-surface shadow-md flex items-center justify-center hover:scale-105 transition-transform">
        <Bell className="w-4.5 h-4.5 w-[18px] h-[18px] text-turq-900 dark:text-turq-300" />
        {data.unread > 0 && (
          <span data-testid="notification-unread-dot"
            className="absolute top-1.5 left-2 min-w-[16px] h-4 px-0.5 rounded-full bg-coral-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse-dot">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
          className="absolute left-0 top-12 w-80 max-h-96 overflow-auto rounded-2xl bg-surface shadow-2xl border border-brd z-50 animate-fade-up">
          <div className="p-3 border-b border-brd font-head font-bold text-sm">التنبيهات</div>
          {data.items.length === 0 && <div className="p-5 text-sm text-mutext text-center">لا توجد تنبيهات بعد</div>}
          {data.items.map((n) => (
            <div key={n.id} data-testid={`notification-item-${n.id.slice(0, 8)}`}
              className={`p-3 text-sm border-b border-brd last:border-0 ${n.read ? "opacity-70" : "bg-turq-50 dark:bg-surface2"}`}>
              <p className="leading-relaxed">{n.text}</p>
              <span className="text-[11px] text-mutext">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell({ nav, notifBase, portalName }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const [dark, setDark] = useState(() => localStorage.getItem("ataa-theme") === "dark");
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async () => {
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("تم تغيير كلمة المرور بنجاح");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setPwBusy(false);
    }
  };

  const backToAdmin = async () => {
    try {
      await api.post("/auth/impersonate-back");
      window.location.href = "/admin";
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("ataa-theme", dark ? "dark" : "light");
  }, [dark]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const switchAccount = async () => {
    await logout();
    navigate("/login");
  };

  const today = new Intl.DateTimeFormat("ar-SA", { dateStyle: "full" }).format(new Date());

  return (
    <div className="flex min-h-screen bg-bg">
      {menuOpen && <div className="fixed inset-0 bg-black/45 z-40 md:hidden" onClick={() => setMenuOpen(false)} />}
      <aside data-testid="sidebar"
        className={`w-60 shrink-0 flex flex-col gap-6 p-5 text-white fixed md:sticky top-0 right-0 h-screen z-50 transition-transform duration-300 ${menuOpen ? "translate-x-0" : "translate-x-full md:translate-x-0"}`}
        style={{ background: "linear-gradient(190deg,#0B6E7A,#0F8896 70%)" }}>
        <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)}
          className="md:hidden absolute top-4 left-4 w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
        <div className="px-1">
          <div className="font-head font-black text-2xl leading-tight">سجل عطاء وأثر</div>
          <div className="font-head font-bold text-sm text-turq-300">تعليم الشرقية</div>
          <div className="text-[11px] text-white/60 mt-1">«نرصد العطاء … ونقيس الأثر»</div>
          <div className="text-[10px] text-white/50 mt-2 leading-relaxed">{portalName}</div>
        </div>
        <nav className="flex flex-col gap-1 overflow-y-auto">
          {nav.map((item) => {
            if (!item.children) {
              return (
                <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.tid} onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] transition-colors ${
                      isActive ? "bg-coral-600 font-bold shadow-lg shadow-coral-600/30" : "text-white/80 hover:bg-white/10"}`}>
                  <item.icon className="w-[17px] h-[17px] shrink-0" />
                  {item.label}
                </NavLink>
              );
            }
            const isOpen = openGroups[item.to] ?? location.pathname.startsWith(item.to);
            return (
              <div key={item.to}>
                <div className="flex items-center gap-1">
                  <NavLink to={item.to} end={item.end} data-testid={item.tid} onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex-1 flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] transition-colors ${
                        isActive ? "bg-coral-600 font-bold shadow-lg shadow-coral-600/30" : "text-white/80 hover:bg-white/10"}`}>
                    <item.icon className="w-[17px] h-[17px] shrink-0" />
                    {item.label}
                  </NavLink>
                  <button data-testid={`${item.tid}-toggle`} aria-label="توسيع القائمة الجانبية"
                    onClick={() => setOpenGroups((g) => ({ ...g, [item.to]: !isOpen }))}
                    className="w-8 h-8 rounded-lg text-white/70 hover:bg-white/10 flex items-center justify-center shrink-0">
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {isOpen && (
                  <div data-testid={`${item.tid}-submenu`} className="mr-5 mt-1 flex flex-col gap-0.5 border-r border-white/15 pr-2 animate-fade-up">
                    {item.children.map((sub) => {
                      const subActive = location.pathname + location.hash === sub.to;
                      return (
                        <Link key={sub.to} to={sub.to} data-testid={sub.tid} onClick={() => setMenuOpen(false)}
                          className={`px-3 py-2 rounded-lg text-[12.5px] transition-colors ${
                            subActive ? "bg-white/15 font-bold" : "text-white/70 hover:bg-white/10"}`}>
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="mt-auto flex items-center gap-2.5 p-3 bg-white/10 rounded-xl">
          <div className="w-9 h-9 rounded-full bg-coral-600 flex items-center justify-center font-head font-bold text-sm shrink-0">
            {(user?.name || "؟").trim().charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-bold truncate">{user?.name}</div>
            <div className="text-[10.5px] text-white/60 truncate">{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} title="تسجيل الخروج"
            className="p-1.5 rounded-lg hover:bg-white/15 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {user?.imp_by && (
          <div data-testid="impersonation-banner"
            className="bg-coral-600 text-white text-xs font-bold px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap">
            <span>وضع المعاينة — تتصفح بوابة المنسق بصلاحية المدير العام</span>
            <button data-testid="impersonation-back-btn" onClick={backToAdmin}
              className="bg-white text-coral-900 rounded-full px-4 py-1 hover:bg-coral-100 transition-colors">
              العودة لبوابة المدير
            </button>
          </div>
        )}
        <header className="flex items-center justify-between gap-4 px-6 lg:px-8 pt-5 pb-2 flex-wrap">
          <div>
            <div className="font-head font-black text-lg text-turq-900 dark:text-turq-300">سجل عطاء وأثر تعليم الشرقية</div>
            <div className="text-xs text-mutext">قسم المسؤولية المجتمعية والعمل التطوعي</div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)}
              className="md:hidden w-10 h-10 rounded-full bg-surface shadow-md flex items-center justify-center">
              <Menu className="w-[18px] h-[18px] text-turq-900 dark:text-turq-300" />
            </button>
            <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} title="تغيير كلمة المرور"
              className="w-10 h-10 rounded-full bg-surface shadow-md flex items-center justify-center hover:scale-105 transition-transform">
              <KeyRound className="w-[18px] h-[18px] text-turq-900 dark:text-turq-300" />
            </button>
            <span className="hidden sm:inline-block bg-surface rounded-full px-4 py-2 text-xs text-mutext shadow-md">{today}</span>
            <button data-testid="switch-account-btn" onClick={switchAccount} title="تبديل الحساب"
              className="w-10 h-10 rounded-full bg-surface shadow-md flex items-center justify-center hover:scale-105 transition-transform">
              <ArrowLeftRight className="w-[18px] h-[18px] text-coral-600" />
            </button>
            <button data-testid="theme-toggle-btn" onClick={() => setDark(!dark)}
              className="w-10 h-10 rounded-full bg-surface shadow-md flex items-center justify-center hover:scale-105 transition-transform">
              {dark ? <Sun className="w-[18px] h-[18px] text-coral-600" /> : <Moon className="w-[18px] h-[18px] text-turq-900" />}
            </button>
            <NotificationBell notifBase={notifBase} />
          </div>
        </header>
        <main className="flex-1 px-6 lg:px-8 pb-10 pt-3">
          <Outlet />
        </main>
      </div>

      {pwOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm" onClick={() => setPwOpen(false)}>
          <div data-testid="change-password-modal" onClick={(e) => e.stopPropagation()}
            className="bg-surface rounded-2xl shadow-2xl border border-brd w-full max-w-sm p-5 animate-fade-up">
            <h3 className="font-head font-bold text-base mb-4">تغيير كلمة المرور</h3>
            <div className="space-y-3">
              <input data-testid="current-password-input" type="password" placeholder="كلمة المرور الحالية" dir="ltr"
                value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="w-full rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-turq-600/40" />
              <input data-testid="new-password-input" type="password" placeholder="كلمة المرور الجديدة (8 أحرف فأكثر)" dir="ltr"
                value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="w-full rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-turq-600/40" />
              <button data-testid="change-password-submit" onClick={changePassword} disabled={pwBusy}
                className="w-full rounded-xl bg-coral-600 hover:bg-coral-900 disabled:opacity-50 text-white font-bold py-2.5 text-sm transition-colors">
                {pwBusy ? "جارٍ الحفظ …" : "تغيير كلمة المرور"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const supNav = [
  { to: "/sup", label: "لوحة المتابعة", icon: LayoutDashboard, end: true, tid: "nav-sup-dashboard" },
];

export function PortalLayout() {
  const { user } = useAuth();
  const nav = coordNav.filter((i) => (i.to !== "/portal/students" && i.to !== "/portal/forty-hours") || !user || !user.school_stage || user.school_stage === "ثانوية");
  return <Shell nav={nav} notifBase="/portal/notifications" portalName="بوابة منسّق التطوع — المحور التشغيلي للمدرسة" />;
}

export function AdminLayout() {
  return <Shell nav={adminNav} notifBase="/admin/notifications" portalName="بوابة الإدارة العامة — قسم المسؤولية المجتمعية والعمل التطوعي" />;
}

export function SupLayout() {
  return <Shell nav={supNav} notifBase="/sup/notifications" portalName="بوابة فريق المتابعة — الإشراف على قطاعات المدارس" />;
}
